import React from 'react';
import { LabeledInput, LabeledSelect, LabeledTextarea } from './Fields';
import type { Settings } from './types';

interface Props {
  selectedHandlerKey?: string;
  selectedHandlerConfigJson?: string;
  inputsSchemaJson: string;
  outputsSchemaJson: string;
  outputsBindingsJson: string;
  settings: Settings;
  definitionPreview: string;
  onChangeInputsSchemaJson: (value: string) => void;
  onChangeOutputsSchemaJson: (value: string) => void;
  onChangeOutputsBindingsJson: (value: string) => void;
  onSyncInputsSchemaFromBindings: () => void;
  onSyncOutputsSchemaFromBindings: () => void;
  onValidateWiring: () => void;
  wiringWarnings: string[];
  onChangeSettings: (next: Settings) => void;
}

export default function JsonAndSettingsCards({
  selectedHandlerKey,
  selectedHandlerConfigJson,
  inputsSchemaJson,
  outputsSchemaJson,
  outputsBindingsJson,
  settings,
  definitionPreview,
  onChangeInputsSchemaJson,
  onChangeOutputsSchemaJson,
  onChangeOutputsBindingsJson,
  onSyncInputsSchemaFromBindings,
  onSyncOutputsSchemaFromBindings,
  onValidateWiring,
  wiringWarnings,
  onChangeSettings,
}: Props) {
  return (
    <>
      <div className="pac-glass rounded-md p-3 space-y-2">
        <h4 className="text-sm font-semibold text-neutral-200">Definition preview</h4>
        <LabeledTextarea
          label=""
          value={definitionPreview}
          onChange={() => { /* read-only */ }}
          rows={10}
          readOnly
        />
      </div>

      {selectedHandlerConfigJson?.trim() ? (
        <div className="pac-glass rounded-md p-3 space-y-2">
          <h4 className="text-sm font-semibold text-neutral-200">Handler config (read-only)</h4>
          <LabeledTextarea
            label={selectedHandlerKey ? `Handler: ${selectedHandlerKey}` : ''}
            value={selectedHandlerConfigJson}
            onChange={() => { /* read-only */ }}
            rows={8}
            readOnly
            description="Contract from handler registry. The workflow stores bindings/defaults separately."
          />
        </div>
      ) : null}

      <div className="pac-glass rounded-md p-3 space-y-3">
        <h4 className="text-sm font-semibold text-neutral-200">Inputs / Outputs / Settings</h4>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
            onClick={onSyncInputsSchemaFromBindings}
          >
            Sync Inputs Schema from Bindings
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
            onClick={onSyncOutputsSchemaFromBindings}
          >
            Sync Outputs Schema from Bindings
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
            onClick={onValidateWiring}
          >
            Validate Wiring
          </button>
        </div>

        {wiringWarnings.length > 0 && (
          <div className="text-xs text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded px-3 py-2 space-y-1">
            {wiringWarnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          <LabeledTextarea
            label="Inputs schema (JSON Schema)"
            value={inputsSchemaJson}
            onChange={onChangeInputsSchemaJson}
            rows={8}
            description="This defines what runtime inputs the executor must request (e.g. inputs.video_url)."
          />
        </div>

        <div className="grid grid-cols-1 gap-3">
          <LabeledTextarea
            label="Outputs bindings (JSON)"
            value={outputsBindingsJson}
            onChange={onChangeOutputsBindingsJson}
            rows={5}
            description={'Map workflow outputs to step outputs. Example: {"result": "{{steps.step_3.outputs.value}}"}.'}
          />
        </div>

        <div className="grid grid-cols-1 gap-3">
          <LabeledTextarea
            label="Outputs schema (JSON Schema)"
            value={outputsSchemaJson}
            onChange={onChangeOutputsSchemaJson}
            rows={8}
            description="This defines the shape of what the workflow returns to the caller."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LabeledSelect
            label="Empty selection policy"
            value={settings.empty_selection_policy ?? 'noop'}
            options={[
              { value: 'noop', label: 'noop (success, do nothing)' },
              { value: 'error', label: 'error (require inputs)' },
            ]}
            onChange={(v) => onChangeSettings({ ...settings, empty_selection_policy: (v === 'error' ? 'error' : 'noop') })}
            description="If step conditions exclude all steps: noop creates a successful run with 0 steps; error rejects the trigger request."
          />
          <LabeledInput
            label="Max concurrency"
            value={String(settings.max_concurrency)}
            onChange={(v) => onChangeSettings({ ...settings, max_concurrency: Number.parseInt(v || '1', 10) || 1 })}
          />
          <LabeledInput
            label="Timeout seconds"
            value={String(settings.timeout_seconds)}
            onChange={(v) => onChangeSettings({ ...settings, timeout_seconds: Number.parseInt(v || '3600', 10) || 3600 })}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LabeledInput
            label="Default retry count"
            value={String(settings.default_retry.count)}
            onChange={(v) => onChangeSettings({ ...settings, default_retry: { ...settings.default_retry, count: Number.parseInt(v || '0', 10) || 0 } })}
          />
          <LabeledInput
            label="Default retry backoff"
            value={settings.default_retry.backoff}
            onChange={(v) => onChangeSettings({ ...settings, default_retry: { ...settings.default_retry, backoff: v } })}
          />
        </div>
      </div>
    </>
  );
}
